import { Suspense } from 'react';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';
import OutOfStockSlash from './OutOfStockSlash';
import { useSwatchSelection } from './use-swatch-selection';

const SWATCH_FRAME =
  'relative z-9 aspect-4/5 w-full border-2 transition-colors duration-100';
const SWATCH_BUTTON =
  'group absolute inset-0.75 overflow-hidden rounded-none border border-none cursor-pointer';
const SWATCH_IMAGE =
  'pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat flex items-center justify-center';

/**
 * Color swatches drawn as Contentstack images with the option value captioned
 * underneath.
 */
export default function ImageSwatches({ selectorSettings, optionName }) {
  const swatches = (selectorSettings.images ?? []).map((item) => ({
    value: item.image_title,
    // TODO: Why are there so many shapes, check whether this should just be
    // item.image?.url and remove the fallback shapes if they're dead.
    imageUrl: item.image?.url || item.image?.[0]?.url || item.url || item.src
  }));

  return (
    <Suspense
      fallback={<ImageSwatchGrid swatches={swatches} {...SKELETON_PROPS} />}
    >
      <SelectedImageSwatchGrid swatches={swatches} optionName={optionName} />
    </Suspense>
  );
}

/**
 * Reads the selected variant, so it needs to stay inside a Suspense boundary.
 * Renders the same grid as the fallback with the selection filled in.
 */
const SelectedImageSwatchGrid = ({ swatches, optionName }) => {
  const selection = useSwatchSelection(optionName);

  return (
    <ImageSwatchGrid
      swatches={swatches}
      selection={selection}
      data-cy="image-swatches-selector"
    />
  );
};

/**
 * The grid itself, presentational
 */
const ImageSwatchGrid = ({ swatches, selection, ...fieldsetProps }) => (
  <fieldset {...fieldsetProps}>
    <legend className="sr-only">Select color</legend>
    <div
      className="grid w-full auto-rows-max grid-cols-4 gap-2.5"
      role="radiogroup"
      aria-label="Color options"
    >
      {swatches.map(({ value, imageUrl }) => {
        const isSelected = selection?.currentValue === value;
        const isOutOfStock = selection?.isOutOfStock(value) ?? false;

        const frameBorder = isSelected
          ? 'border-light-green'
          : isOutOfStock
            ? 'border-parlor-green-60 opacity-100'
            : 'group-hover:border-parlor-green-60 border-transparent';

        return (
          <div
            className="group relative flex flex-col items-start justify-start"
            key={value}
          >
            <div className={`${SWATCH_FRAME} ${frameBorder}`}>
              <button
                type="button"
                value={value}
                onClick={selection && (() => selection.selectOption(value))}
                className={SWATCH_BUTTON}
                aria-label={`Color Option: ${value}`}
                aria-pressed={selection ? isSelected : undefined}
              >
                <div
                  className={SWATCH_IMAGE}
                  style={{
                    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none'
                  }}
                />
              </button>

              {isOutOfStock && (
                <OutOfStockSlash
                  svgClassName="stroke-parlor-green-60 pointer-events-none absolute h-full w-full"
                  viewBox="2 2 100 100"
                  x2="105"
                  y2="105"
                />
              )}
            </div>

            <span className="text-crisp-white mt-1 ml-1.25 w-full self-start text-left text-xs leading-tight">
              {value}
            </span>
          </div>
        );
      })}
    </div>
  </fieldset>
);
